import { useQuery } from '@tanstack/react-query'
import { useAuth, useOrganization } from '@clerk/clerk-react'
import { AGENT_URL } from '../config/env'
import { orgMetricsKeys } from '@/lib/queryKeys'

export interface OrgMetricsSummary {
  duties_count: number
  calls_count: number
  patients_count: number
  computer_use_sessions_count: number
}

export interface OrgMetrics {
  summary: OrgMetricsSummary
  // Full-time-worker equivalent of the org's agent workload: the billing FTWE
  // for the month ftw_period_start names (server-computed). Null for an org
  // with no rate card, which has nothing to estimate from.
  ftw_estimate: number | null
  // ISO date of the first day of the month ftw_estimate covers.
  ftw_period_start: string
  is_aggregate: boolean // true when viewing as super user (all orgs aggregated)
  generated_at: string
}

async function fetchOrgMetrics(token: string): Promise<OrgMetrics> {
  const response = await fetch(`${AGENT_URL}/org/metrics`, {
    headers: {
      Authorization: token,
    },
  })

  if (!response.ok) {
    const errorText = await response.text()
    let errorMessage = `Failed to fetch metrics: ${response.status}`
    try {
      const errorData = JSON.parse(errorText)
      errorMessage = errorData.error || errorMessage
    } catch {
      if (errorText) errorMessage = errorText
    }
    throw new Error(errorMessage)
  }

  return response.json()
}

/**
 * Hook to fetch organization metrics summary counts.
 *
 * Metrics are automatically refetched when the organization changes.
 * Results are cached for 30 seconds (staleTime) to keep dashboard data fresh.
 *
 * @returns Query result with org metrics data
 */
export function useOrgMetrics() {
  const { getToken } = useAuth()
  const { organization } = useOrganization()

  return useQuery({
    queryKey: orgMetricsKeys.all(organization?.id),
    queryFn: async () => {
      const token = await getToken()
      if (!token) {
        throw new Error('No authentication token available')
      }
      return fetchOrgMetrics(token)
    },
    enabled: !!organization?.id,
    staleTime: 30_000, // dashboard should stay fresh
    refetchInterval: 30_000, // refresh metrics every 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes cache retention
    refetchOnWindowFocus: false,
  })
}
